import { commandAdminProblemReport } from '../../../api/generated/problem-reports/problem-reports'
import type { ProblemReportCommand } from '../../../api/generated/model/problemReportCommand'
import {
  assertReportRequest,
  reportHostState,
  reportRequestContext,
  settleReportRequest,
  subscribeReportHost,
} from '../../../lib/problem-reporting/context'
import { submissionError, type SubmissionError } from '../submission'
export type AdminAction = Omit<
  ProblemReportCommand,
  'command_key' | 'expected_revision'
>
export function createAdminCommands() {
  type State = {
    id: string | null
    outcome: 'idle' | 'pending' | 'unknown' | 'conflict' | 'saved' | 'rejected'
    error: SubmissionError | null
    revision: number | null
  }
  let state: State = { id: null, outcome: 'idle', error: null, revision: null }
  let frozen: {
    id: string
    body: ProblemReportCommand
    context: ReturnType<typeof reportRequestContext>
    uncertain: boolean
  } | null = null
  let generation = reportHostState().generation
  let request: AbortController | null = null
  let disposed = false
  const listeners = new Set<() => void>()
  const publish = (patch: Partial<State>) => {
    if (!disposed) {
      state = { ...state, ...patch }
      listeners.forEach((listener) => listener())
    }
  }
  const stop = subscribeReportHost(() => {
    if (generation !== reportHostState().generation) {
      generation = reportHostState().generation
      request?.abort()
      request = null
      frozen = null
      publish({ id: null, outcome: 'idle', error: null, revision: null })
    }
  })
  async function execute() {
    if (disposed || request || !frozen) return
    const attempt = frozen
    const host = reportHostState()
    // Receipt lookup still needs current review + manage authority, but not rollout ON.
    if (
      host.phase !== 'ready' ||
      host.identity?.shell !== 'admin' ||
      !host.policy?.can_review ||
      !host.policy.can_manage
    )
      return
    assertReportRequest(attempt.context)
    const controller = new AbortController()
    request = controller
    publish({
      id: attempt.id,
      outcome: 'pending',
      error: null,
      revision: null,
    })
    try {
      const response = await commandAdminProblemReport(
        attempt.id,
        attempt.body,
        {
          signal: controller.signal,
          headers: {
            'X-Problem-Report-Context': attempt.context.identity.membershipId,
          },
        },
      )
      await settleReportRequest(attempt.context, controller.signal)
      if (response.status !== 200)
        throw { status: response.status, problem: response.data }
      frozen = null
      publish({ outcome: 'saved', revision: response.data.applied_revision })
    } catch (error) {
      if (
        disposed ||
        controller.signal.aborted ||
        generation !== attempt.context.generation
      )
        return
      const safe = submissionError(error)
      if (
        attempt.uncertain ||
        !safe.status ||
        safe.status >= 500 ||
        safe.status === 408
      ) {
        attempt.uncertain = true
        publish({ outcome: 'unknown', error: safe })
      } else {
        frozen = null
        publish({
          outcome: safe.status === 409 ? 'conflict' : 'rejected',
          error: safe,
        })
      }
    } finally {
      if (request === controller) request = null
    }
  }
  return {
    getState: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    async send(id: string, revision: number, action: AdminAction) {
      if (disposed || request) return
      if (!frozen) {
        const host = reportHostState()
        if (
          host.phase !== 'ready' ||
          host.identity?.shell !== 'admin' ||
          !host.policy?.enabled ||
          !host.policy.can_review ||
          !host.policy.can_manage
        )
          return
        frozen = {
          id,
          body: structuredClone({
            ...action,
            command_key: crypto.randomUUID(),
            expected_revision: revision,
          }),
          context: reportRequestContext(),
          uncertain: false,
        }
      }
      await execute()
    },
    retry: execute,
    dispose() {
      disposed = true
      stop()
      request?.abort()
      frozen = null
      listeners.clear()
    },
  }
}
